// @noflow
import { useLanguage } from '@/context/injectedValues/injectedValues'
import { useLocalisation } from '@/context/localisation'
import Confetti from '@/lottie/welcome-back-confetti.json'
import { useQuery } from '@apollo/client'
import { formatDuration, intervalToDuration, isBefore } from 'date-fns'
import isNil from 'lodash/isNil'
import isNull from 'lodash/isNull'
import isUndefined from 'lodash/isUndefined'
import Lottie from 'lottie-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { toLocalisedSentence } from '@/utils/StringHelper'

import Illustration from 'assets/images/illustrations/boxes/box-full-of-pouches-excited-dog.svg'

import Card from '@/components/elements/atoms/Card/Card'
import { CardSkeleton } from '@/components/elements/atoms/Card/CardSkeleton'
import Text from '@/components/elements/atoms/Text/Text'

import STYLES from './WelcomeBackCard.module.sass'

import { WELCOME_BACK_CARD_QUERY } from './queries'

const WelcomeBackCard = (): JSX.Element | null => {
  const { t } = useTranslation()
  const { dateLocale } = useLocalisation()
  const { userLanguage } = useLanguage()
  const namespace = 'account'
  const [timeUntilCutOff, setTimeUntilCutOff] = useState<string | null>(null)

  const { loading, data, error } = useQuery(WELCOME_BACK_CARD_QUERY)

  const cutOffDateAndTime = useMemo(() => {
    if (isNil(data?.user?.subscription?.nextEditableBox?.cutOffDate)) {
      return undefined
    }

    const date = new Date(data?.user?.subscription?.nextEditableBox?.cutOffDate)
    date.setHours(23)
    date.setMinutes(0)
    date.setSeconds(0)
    date.setMilliseconds(0)

    return date
  }, [data?.user?.subscription?.nextEditableBox?.cutOffDate])

  const shouldSeeEditableCutOffText = useMemo(() => {
    return (
      data?.user?.subscription?.nextEditableBox?.amendable &&
      cutOffDateAndTime &&
      isBefore(new Date(), cutOffDateAndTime)
    )
  }, [data?.user?.subscription?.nextEditableBox?.amendable, cutOffDateAndTime])

  const calculateTimeUntilDate = useCallback(
    (until: Date, setter: (timeUntil: string) => void) => {
      const now = new Date()

      const duration = intervalToDuration({
        start: now,
        end: until
      })

      const lessThanOneHourRemaining =
        duration.months === 0 &&
        duration.days === 0 &&
        duration.hours === 0 &&
        duration.minutes &&
        duration.minutes > 0

      // Format the duration based on its length
      let formattedDuration
      if (lessThanOneHourRemaining) {
        formattedDuration = formattedDuration = formatDuration(
          { hours: 1 },
          {
            format: ['hours'],
            delimiter: '||',
            locale: dateLocale
          }
        )
      } else if (duration.months && duration.months > 0) {
        formattedDuration = formatDuration(duration, {
          format: ['months', 'days'],
          delimiter: '||',
          locale: dateLocale
        })
      } else {
        formattedDuration = formatDuration(duration, {
          format: ['days', 'hours'],
          delimiter: '||',
          locale: dateLocale
        })
      }

      // Convert the formatted duration to a localised sentence
      const localisedTime = toLocalisedSentence({
        arr: formattedDuration.split('||'),
        lng: userLanguage
      })

      // Update the state
      setter(localisedTime)
    },
    [dateLocale, userLanguage]
  )

  // Handle the cutOffDate and deliveryDate change
  useEffect(() => {
    if (shouldSeeEditableCutOffText && cutOffDateAndTime) {
      calculateTimeUntilDate(cutOffDateAndTime, setTimeUntilCutOff)
    } else {
      setTimeUntilCutOff(null)
    }
  }, [calculateTimeUntilDate, shouldSeeEditableCutOffText, cutOffDateAndTime])

  if (loading && !error) {
    return (
      <div className={STYLES.cardWrapper}>
        <CardSkeleton height="20rem" width="100%" />
      </div>
    )
  }
  // eslint-disable-next-line i18next/no-literal-string
  if (error)
    throw new Error(
      `Error when WELCOME_BACK_CARD_QUERY executes: ${error.message}`
    )

  if (isUndefined(data) || isNull(data)) return null

  const { user } = data || {}
  const { firstName } = user || {}

  return (
    <div className={STYLES.cardWrapper}>
      <div className={STYLES.confetti}>
        <Lottie animationData={Confetti} loop={false} />
      </div>
      <Card border="none" padding={0} shadow>
        <div className={STYLES.cardContent}>
          <div className={STYLES.cardCopy}>
            <Text
              namespace={namespace}
              text={`price_sensitive_paused_page.welcome_back_card.title`}
              variant="display20"
              margin={false}
              variables={{ firstName: firstName }}
            />
            <Text
              namespace={namespace}
              text={`price_sensitive_paused_page.welcome_back_card.body`}
              margin={false}
              variables={{ timeTilCutoff: timeUntilCutOff }}
            />
          </div>
          <div className={STYLES.cardImage}>
            <img
              src={Illustration}
              alt={t(
                'account:price_sensitive_paused_page.welcome_back_card.alt'
              )}
              width={190}
              height={150}
            />
          </div>
        </div>
      </Card>
    </div>
  )
}

export default WelcomeBackCard
